/**
 * @fileoverview ThreadsClient factory for KorvoChat
 * 
 * @purpose Creates ThreadsClient instances from KorvoChat config for consistent
 *          Portal SDK integration across the component.
 * 
 * @usage Used internally by KorvoChat to create ThreadsClient when not provided as prop.
 * 
 * @ownership KorvoChat component
 * 
 * @dependencies
 * - @korvo/portal-sdk (ThreadsClient)
 * 
 * @restrictions
 * - DO NOT use in browser-only contexts without window check
 * - DO NOT hardcode base URLs - use environment detection
 * 
 * @docs
 * - docs/INTEGRATION_PLAN_KORVOCHAT_PORTAL_SDK.md (integration plan)
 */

import ThreadsClient from '@korvo/portal-sdk/threads-client'

import type { KorvoChatConfig } from './types'

/**
 * Creates a ThreadsClient instance from KorvoChat config
 * 
 * @param config - KorvoChat configuration
 * @returns ThreadsClient instance configured for the module
 */
export function createThreadsClient(config: KorvoChatConfig): ThreadsClient {
  // Determine base URL
  let baseUrl: string;
  if (typeof window !== 'undefined') {
    // Client-side: use current origin
    baseUrl = window.location.origin;
  } else {
    // Server-side (SSR): Use relative URLs (empty string = same origin)
    // This avoids requiring Node.js scripts in client components
    // ThreadsClient will make requests relative to the current request origin
    baseUrl = '';
  }
  
  // Extract API base from config.portal.apiEndpoint
  // e.g., '/api/chat' -> '/api/chat'
  // e.g., 'api/chat' -> '/api/chat'
  let apiBase = config.portal.apiEndpoint
  if (!apiBase.startsWith('/')) {
    apiBase = `/${apiBase}`
  }
  
  return new ThreadsClient(baseUrl, apiBase)
}

